import { API_ENDPOINTS } from "@/constants/api";
import type { FeatureKey, FeaturePermissions } from "@/constants/permissions";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  createdAt: string;
}

export interface Membership {
  id: string;
  role: string;
}

export interface PendingInvitation {
  id: string;
  organizationName: string;
  inviterName: string;
  role: string | null;
  expiresAt: string;
}

export interface OrganizationMe {
  organization: Organization | null;
  membership: Membership | null;
  permissions: FeaturePermissions | null;
  pendingInvitation: PendingInvitation | null;
  graceMode: boolean;
  provisioningFailed: boolean;
}

export interface OrgMember {
  id: string;
  role: string;
  permissions: Partial<Record<FeatureKey, boolean>> | null;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
  };
}

export interface OrgInvitation {
  id: string;
  email: string;
  role: string | null;
  status: string;
  expiresAt: string;
  inviter: { name: string; email: string };
}

export interface PublicInvitation {
  id: string;
  organizationName: string;
  inviterName: string;
  email: string;
  role: string | null;
  status: string;
  expiresAt: string;
}

export interface AuditLogEntry {
  id: string;
  createdAt: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  method: string | null;
  path: string | null;
  payload: unknown;
  status: number | null;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface AuditLogPage {
  items: AuditLogEntry[];
  nextCursor: string | null;
}

export interface AuditLogFilters {
  actorUserId?: string;
  action?: string;
  from?: string;
  to?: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(
      (body as { error?: string }).error || "Request failed"
    ) as Error & { code?: string; status?: number };
    error.code = (body as { code?: string }).code;
    error.status = res.status;
    throw error;
  }
  return (body as { data: T }).data;
};

export const OrganizationService = {
  getMe: (): Promise<OrganizationMe> => request(API_ENDPOINTS.ORGANIZATION.ME),

  rename: (name: string): Promise<{ name: string }> =>
    request(API_ENDPOINTS.ORGANIZATION.BASE, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  getMembers: (): Promise<OrgMember[]> => request(API_ENDPOINTS.ORGANIZATION.MEMBERS),

  updateMemberRole: (memberId: string, role: string): Promise<OrgMember> =>
    request(API_ENDPOINTS.ORGANIZATION.MEMBER_BY_ID(memberId), {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),

  updateMemberPermissions: (
    memberId: string,
    permissions: Partial<Record<FeatureKey, boolean>>
  ): Promise<OrgMember> =>
    request(API_ENDPOINTS.ORGANIZATION.MEMBER_PERMISSIONS(memberId), {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    }),

  removeMember: (memberId: string): Promise<{ removed: boolean }> =>
    request(API_ENDPOINTS.ORGANIZATION.MEMBER_BY_ID(memberId), { method: "DELETE" }),

  getInvitations: (): Promise<OrgInvitation[]> =>
    request(API_ENDPOINTS.ORGANIZATION.INVITATIONS),

  createInvitation: (email: string, role: "admin" | "member"): Promise<OrgInvitation> =>
    request(API_ENDPOINTS.ORGANIZATION.INVITATIONS, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  cancelInvitation: (invitationId: string): Promise<{ canceled: boolean }> =>
    request(API_ENDPOINTS.ORGANIZATION.INVITATION_BY_ID(invitationId), {
      method: "DELETE",
    }),

  getPublicInvitation: (invitationId: string): Promise<PublicInvitation> =>
    request(API_ENDPOINTS.ORGANIZATION.INVITATION_PUBLIC(invitationId)),

  acceptInvitation: (
    invitationId: string
  ): Promise<{ organization: Organization; membership: Membership | null }> =>
    request(API_ENDPOINTS.ORGANIZATION.INVITATION_ACCEPT(invitationId), {
      method: "POST",
    }),

  getAuditLogs: (filters: AuditLogFilters, cursor?: string, limit = 25): Promise<AuditLogPage> => {
    const url = new URL(API_ENDPOINTS.ORGANIZATION.AUDIT_LOGS, window.location.origin);
    if (filters.actorUserId) url.searchParams.set("actorUserId", filters.actorUserId);
    if (filters.action) url.searchParams.set("action", filters.action);
    if (filters.from) url.searchParams.set("from", filters.from);
    if (filters.to) url.searchParams.set("to", filters.to);
    if (cursor) url.searchParams.set("cursor", cursor);
    url.searchParams.set("limit", String(limit));
    return request(url.toString());
  },
};
